import { useState } from "react";
import { yerelGunBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  MetinAlani,
  Secim,
  Sekmeler,
  cn,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  baskasininTakvimi,
  cokluTakvim,
  kapasiteTablosu,
  kendiTakvimim,
  kurumEtkinligiEkle,
  kurumTakvimi,
  musaitlikBul,
  takvimKatilimcilari,
  personelListesi,
  type Aralik,
  type TakvimEtkinligi,
} from "./api";
import { izinVarMi, type IzinHaritasi } from "./izin";
import { MesguliyetKaydi } from "./KucukFormlar";

/**
 * TAKVIM — F-CAL, matris §5.12
 *
 * ================================================================
 * MESGULIYET MODUNDA BASLIK ALANI CEVAPTA HIC YOK
 * ================================================================
 * Sunucu satis danismanina yalnizca dolu/bos donuyor
 * (`takvim.baskasi.ayrinti` izni onda yok). Cevap ayrik birlesim:
 * `mod: "MESGULIYET"` olan bir etkinlikte `title` alani BULUNMUYOR —
 * bos degil, YOK.
 *
 * Ekran bu yuzden basligi `e.title ?? "Meşgul"` gibi okumuyor;
 * once `mod`a bakiyor. Boyle yazilmasaydi TypeScript zaten derlemezdi,
 * ama daha onemlisi: gorunmemesi gereken bir alani "varsa goster"
 * diye yazmak, o alanin bir gun cevaba eklenmesi halinde sessizce
 * sizmasina yol acardi.
 */

/**
 * Bugünden başlayan yedi günlük pencere.
 *
 * `yerelGunBasi` — eski hâli `getUTCDate()` kullanıyordu ve Türkiye
 * UTC+3 olduğu için yerel 00:00–02:59 arasında pencereyi **bir gün
 * geriye** açıyordu: gece yarısından sonra takvime bakan uzman dünün
 * gününü görüyor, bugünün son dilimini kaçırıyordu.
 */
function gunAraligi(tarih: Date): Aralik {
  const bas = yerelGunBasi(tarih);
  return {
    baslangic: bas.toISOString(),
    bitis: new Date(bas.getTime() + 7 * 24 * 3600_000).toISOString(),
  };
}

function saat(iso: string): string {
  return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function gun(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/**
 * EKİP TAKVİMİ İKİ İZNE BAĞLI — rol adına DEĞİL (rol testi H9, C61).
 *
 * Ekran önce kişi listesini (`GET /personel`, `kisi.personel.goruntule`) sonra seçilenlerin takvimini
 * (`POST /takvim/coklu`, `takvim.baskasi.goruntule`) okuyor. Koçta ikisi de yoktu ama sekme görünüyordu;
 * tıklayınca 403 dönüyordu. İzin haritası verilmemişse (eski çağrı yerleri) sekme gizlenmez: karar sunucuda.
 */
export function ekipTakvimiGorunur(izinler: IzinHaritasi | undefined): boolean {
  if (izinler === undefined) return true;
  return (
    izinVarMi(izinler, "kisi.personel.goruntule") && izinVarMi(izinler, "takvim.baskasi.goruntule")
  );
}

export function Takvim({ izinler }: { izinler?: IzinHaritasi } = {}) {
  const [gorunum, setGorunum] = useState<"benim" | "ekip" | "kurum" | "musaitlik" | "kapasite">(
    "benim",
  );
  const ekipGorunur = ekipTakvimiGorunur(izinler);

  return (
    <div className="flex flex-col gap-4">
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — fark ekran okuyucuda.

        Elle yazılmış `role="tab"` şeridi vardı: `aria-selected` koyulmuş
        ama ok tuşlarıyla gezinme, gezinen odak ve `tabpanel` bağı YOKTU.
        Eksik kurulmuş bir ARIA deseni hiç yazmamaktan KÖTÜ — kullanıcıya
        "burada ok tuşlarıyla gezebilirsin" diye söz verip tutmuyor.

        `Sekmeler` `aria-pressed` kullanıyor: aynı listeyi süzen
        basılı-kalan düğmeler, ki gerçekte olan bu.
      */}
      <Sekmeler
        etiket="Takvim görünümü"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "benim", etiket: "Takvimim" },
          ...(ekipGorunur ? [{ deger: "ekip" as const, etiket: "Ekip takvimi" }] : []),
          { deger: "kurum", etiket: "Kurum takvimi" },
          { deger: "musaitlik", etiket: "Müsait saat bul" },
          { deger: "kapasite", etiket: "Uzman kapasitesi" },
        ]}
      />

      {gorunum === "benim" && (
        <div className="flex flex-col gap-4">
          {/*
            MESGULIYET KENDI TAKVIMININ YANINDA: uzman ogrencinin
            okul saatini girerken kendi haftasina bakiyor. Ayri bir
            ekran olsaydi iki pencere arasinda gidip gelirdi.
          */}
          <MesguliyetKaydi />
          <HaftalikGorunum kaynak="benim" />
        </div>
      )}
      {gorunum === "ekip" && ekipGorunur && <EkipGorunumu />}
      {gorunum === "kurum" && (
        <div className="flex flex-col gap-4">
          <KurumEtkinligi />
          <HaftalikGorunum kaynak="kurum" />
        </div>
      )}
      {gorunum === "musaitlik" && <MusaitlikGorunumu />}
      {gorunum === "kapasite" && <KapasiteGorunumu />}
    </div>
  );
}

// ---------------------------------------------------------------
// HAFTALIK GORUNUM
// ---------------------------------------------------------------

function HaftalikGorunum({
  kaynak,
  kisiId,
}: {
  kaynak: "benim" | "kurum" | "kisi";
  /** `kaynak === "kisi"` iken zorunlu — kimin takvimi. */
  kisiId?: string;
}) {
  const aralik = gunAraligi(new Date());

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["takvim", kaynak, kisiId ?? null, aralik.baslangic],
    queryFn: () =>
      kaynak === "benim"
        ? kendiTakvimim(aralik)
        : kaynak === "kurum"
          ? kurumTakvimi(aralik)
          : baskasininTakvimi(kisiId!, aralik),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Takvim alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.etkinlikler.length === 0) {
    return (
      <BosDurum
        baslik={
          kaynak === "benim"
            ? "Bu hafta boş"
            : kaynak === "kurum"
              ? "Kurum takviminde kayıt yok"
              : "Bu kişinin takviminde kayıt yok"
        }
        aciklama={
          kaynak === "kisi"
            ? "Önümüzdeki yedi gün gösteriliyor. Boş görünmesi, size kapalı olduğu anlamına da gelebilir."
            : "Önümüzdeki yedi gün gösteriliyor."
        }
      />
    );
  }

  const gunlere = new Map<string, TakvimEtkinligi[]>();
  for (const e of data.etkinlikler) {
    const anahtar = e.startsAt.slice(0, 10);
    gunlere.set(anahtar, [...(gunlere.get(anahtar) ?? []), e]);
  }

  return (
    <div className="flex flex-col gap-4">
      {[...gunlere.entries()].map(([tarih, etkinlikler]) => (
        <section
          key={tarih}
          className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-4"
        >
          <h3 className="text-notr-900 mb-3 text-sm font-semibold">
            {gun(etkinlikler[0]!.startsAt)}
          </h3>
          <ul className="flex flex-col gap-2">
            {etkinlikler.map((e) => (
              <li key={e.id}>
                <EtkinlikSatiri etkinlik={e} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Tek etkinlik.
 *
 * Daraltma (`e.mod === "AYRINTI"`) OLMADAN `title` okunamiyor —
 * bu tipin tasidigi asil koruma bu.
 */
export function EtkinlikSatiri({ etkinlik }: { etkinlik: TakvimEtkinligi }) {
  const zaman = etkinlik.isAllDay
    ? "Tüm gün"
    : `${saat(etkinlik.startsAt)} – ${saat(etkinlik.endsAt)}`;

  if (etkinlik.mod === "MESGULIYET") {
    return (
      <div className="min-h-dokunma rounded-alan bg-notr-100 flex items-center gap-3 px-3 py-1.5">
        <span className="text-notr-700 shrink-0 text-sm tabular-nums">{zaman}</span>
        <span className="min-w-0">
          {/*
            ================================================================
            `notr-600`, `notr-500` DEĞİL — ÖLÇÜLDÜ
            ================================================================
            `notr-500` kendi `notr-100` zemininde **4,41** veriyor ve
            AA'nın 4,5 eşiğinin altında kalıyor. Beyaz üstünde 4,83 ile
            geçtiği için gözden kaçıyor: zemin koyulaştıkça düşen tek
            şey o. `notr-600` aynı zeminde **6,90**.

            Blok yine geride duruyor — zemini ve `notr-700` saatiyle.
            Geride durmak okunmaz olmayı gerektirmiyor.
          */}
          <span className="text-notr-600 block text-sm">Meşgul</span>
          {/*
            ================================================================
            SEBEP SATIRIN KENDİSİNDE — `Kapali.dc.html`
            ================================================================
            Satır yalnızca "Meşgul" diyordu. O tek kelime **iki ayrı
            şeyi** birden karşılıyordu:

              · kişi o saati kendine meşgul işaretlemiş
              · orada bir etkinlik VAR ama ayrıntısı size kapalı

            Birincisi takvim hakkında bir olgu, ikincisi SİZİN
            yetkiniz hakkında. Ayırt edilmeyince satış danışmanı
            "uzman boş değilmiş" diye okuyup randevu aramayı bırakıyor;
            oysa oradaki şey belki de kendi öğrencisinin görüşmesi.

            Listenin üstünde zaten bir cümle vardı ("Boş görünmesi,
            size kapalı olduğu anlamına da gelebilir") ama o LİSTE
            hakkında; buradaki SATIR hakkında. Sağlık ekranında da
            aynısını yaptık: sebep, sebebi olan şeyin yanında durmalı.
          */}
          <span className="text-notr-600 block text-xs">Bu saatin ayrıntısı size kapalı</span>
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-dokunma rounded-alan bg-notr-50 flex items-center gap-3 px-3">
      <span className="text-notr-700 shrink-0 text-sm tabular-nums">{zaman}</span>
      <span className="min-w-0 flex-1">
        <span className="text-notr-900 block truncate text-sm font-medium">{etkinlik.title}</span>
        {etkinlik.location && (
          <span className="text-notr-600 block truncate text-xs">{etkinlik.location}</span>
        )}
      </span>
      {etkinlik.meetUrl && (
        <a
          href={etkinlik.meetUrl}
          className="text-marka-600 focus-visible:outline-marka-600 shrink-0 text-sm underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          rel="noreferrer"
          target="_blank"
        >
          Görüşme
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// EKIP TAKVIMI — coklu gorunum
// ---------------------------------------------------------------

/**
 * ================================================================
 * MOD SUNUCUDAN GELIYOR, KISI BASINA
 * ================================================================
 * Cevaptaki her kisi kendi `mod`unu tasiyor. Satis danismani icin
 * hepsi `MESGULIYET` gelir ve `EtkinlikSatiri` basligi HIC OKUMAZ —
 * cunku o alan cevapta yoktur.
 *
 * Ekranin ustunde modun ne oldugu ACIKCA yaziyor. Yazilmasaydi satis
 * danismani "takvim bos gorunuyor, sistem bozuk" diye destek talebi
 * acardi; oysa gordugu sey dogru ve kasitli.
 */
function EkipGorunumu() {
  const [secilenler, setSecilenler] = useState<Set<string>>(new Set());
  const [arandi, setArandi] = useState<string[] | null>(null);
  const aralik = gunAraligi(new Date());

  const personel = useQuery({
    queryKey: ["personel", false],
    queryFn: () => personelListesi(false),
    retry: false,
  });

  const adlar = new Map(
    (personel.data?.personel ?? []).map((p) => [p.id, `${p.firstName} ${p.lastName}`]),
  );

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["coklu-takvim", arandi?.join(",")],
    queryFn: () => cokluTakvim(arandi!, aralik),
    enabled: arandi !== null && arandi.length > 0,
  });

  return (
    <div className="flex flex-col gap-4">
      {/*
        ================================================================
        KİMLİK YAPIŞTIRMA YERİNE KİŞİ SEÇİMİ
        ================================================================
        Burada "Kullanıcı kimliklerini boşlukla ayırın" yazan bir metin
        kutusu vardı. Kimse `cms5q65g0009v730…` gibi bir kimliği
        bilmiyor ve panelde onu görebileceği bir yer de yok: ekran
        çalışıyor görünüyordu ama kullanılamıyordu.

        Personel listesi zaten kapsam süzgeçli geliyor; göremediğin
        kişi listede de yok.
      */}
      <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4">
        <h3 className="text-notr-900 text-sm font-semibold">Kimlerin takvimi</h3>

        {personel.isPending ? (
          <Iskelet satir={3} />
        ) : personel.isError ? (
          <p className="text-notr-600 text-sm">Personel listesi alınamadı; kişi seçilemiyor.</p>
        ) : personel.data.personel.length === 0 ? (
          <p className="text-notr-600 text-sm">Kapsamınızda personel görünmüyor.</p>
        ) : (
          <>
            <ul className="flex flex-wrap gap-2">
              {personel.data.personel.map((p) => {
                const secili = secilenler.has(p.id);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      aria-pressed={secili}
                      onClick={() => {
                        const yeniKume = new Set(secilenler);
                        if (secili) yeniKume.delete(p.id);
                        else yeniKume.add(p.id);
                        setSecilenler(yeniKume);
                      }}
                      className={cn(
                        "min-h-dokunma rounded-alan px-3 text-sm",
                        secili
                          ? "bg-marka-600 text-notr-0"
                          : "bg-notr-100 text-notr-700 hover:bg-notr-200",
                      )}
                    >
                      {p.firstName} {p.lastName}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div>
              <Buton disabled={secilenler.size === 0} onClick={() => setArandi([...secilenler])}>
                Takvimleri getir
              </Buton>
            </div>
          </>
        )}
      </div>

      {arandi && isPending && <Iskelet satir={4} />}

      {isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {error instanceof ApiHatasi ? error.message : "Takvimler alınamadı."}
        </p>
      )}

      {data?.map((kisi) => (
        <section
          key={kisi.userId}
          className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-4"
        >
          {/*
            Ad EŞLEŞMEZSE kısaltılmış kimlik gösteriliyor, uydurma bir
            ad değil: iki sorgunun kapsamı aynı ama eşleşmeyen kimlik
            "kapsam dışı" demek.
          */}
          <h3 className="text-notr-900 text-sm font-semibold">
            {adlar.get(kisi.userId) ?? `#${kisi.userId.slice(-6)}`}
          </h3>

          {/* Modun ne oldugu ACIKCA yaziyor — bkz. yukaridaki not. */}
          {kisi.mod === "MESGULIYET" && (
            <p className="text-uyari-600 mt-1 mb-3 text-xs">
              Yetkiniz yalnızca dolu/boş görmeye açık; görüşme başlıkları gösterilmiyor.
            </p>
          )}

          {kisi.etkinlikler.length === 0 ? (
            <p className="text-notr-600 mt-2 text-sm">Bu hafta kayıt yok.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {kisi.etkinlikler.map((e) => (
                <li key={e.id}>
                  <EtkinlikSatiri etkinlik={e} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------
// MUSAITLIK
// ---------------------------------------------------------------

function MusaitlikGorunumu() {
  const [katilimcilar, setKatilimcilar] = useState<Array<{ userId: string; ad: string }>>([]);
  const [arama, setArama] = useState("");
  const kisiler = useQuery({
    queryKey: ["takvim-katilimcilari", arama],
    queryFn: () => takvimKatilimcilari(arama),
    retry: false,
  });
  const [sure, setSure] = useState(45);
  const [arandi, setArandi] = useState<{ kullanicilar: string[]; sure: number } | null>(null);

  const aralik = gunAraligi(new Date());

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["musaitlik", arandi?.kullanicilar.join(","), arandi?.sure],
    queryFn: () =>
      musaitlikBul({
        kullanicilar: arandi!.kullanicilar,
        baslangic: aralik.baslangic,
        bitis: aralik.bitis,
        sureDakika: arandi!.sure,
      }),
    enabled: arandi !== null,
  });

  return (
    <div className="flex flex-col gap-4">
      <form
        className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
        onSubmit={(e) => {
          e.preventDefault();
          const liste = katilimcilar.map((k) => k.userId);
          if (liste.length > 0) setArandi({ kullanicilar: liste, sure });
        }}
      >
        <p className="text-notr-600 text-xs">
          Bu arama yalnızca <strong>saat</strong> döndürür; kimin ne yaptığı görünmez. Göremediğiniz
          birinin doluluğu sonuca karışmaz.
        </p>

        {/* F-CAL-06c — mevcut Alan/Secim deseni; seçenekler takvim kapsamından gelir. */}
        <Alan
          etiket="Katılımcı ara"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          yardim="Personel veya öğrenci adıyla arayın."
        />
        <Secim
          etiket="Katılımcı ekle"
          value=""
          disabled={katilimcilar.length >= 20 || kisiler.isFetching}
          onChange={(e) => {
            const kisi = kisiler.data?.kisiler.find((k) => k.userId === e.target.value);
            if (kisi && !katilimcilar.some((k) => k.userId === kisi.userId))
              setKatilimcilar((k) => [...k, kisi]);
            setArandi(null);
          }}
        >
          <option value="">Personel veya öğrenci seçin</option>
          {kisiler.data?.kisiler
            .filter((k) => !katilimcilar.some((s) => s.userId === k.userId))
            .map((k) => (
              <option key={k.userId} value={k.userId}>
                {k.ad}
                {k.ogrenciNo === null ? " · Personel" : ` · Öğrenci no ${k.ogrenciNo}`}
              </option>
            ))}
        </Secim>
        {kisiler.isFetching && <p role="status">Katılımcılar alınıyor…</p>}
        {kisiler.isError && (
          <p role="alert">
            Katılımcılar alınamadı.{" "}
            <Buton gorunum="sessiz" onClick={() => void kisiler.refetch()}>
              Tekrar dene
            </Buton>
          </p>
        )}
        {kisiler.data?.kisiler.length === 200 && (
          <p>Liste ilk 200 kişiyi gösteriyor; adıyla arayarak daraltın.</p>
        )}
        <ul className="flex flex-wrap gap-2" aria-label="Seçili katılımcılar">
          {katilimcilar.map((k) => (
            <li key={k.userId}>
              {k.ad}{" "}
              <Buton
                gorunum="sessiz"
                aria-label={`${k.ad} katılımcısını kaldır`}
                onClick={() => {
                  setKatilimcilar((liste) => liste.filter((s) => s.userId !== k.userId));
                  setArandi(null);
                }}
              >
                Kaldır
              </Buton>
            </li>
          ))}
        </ul>

        <div className="w-32">
          <Alan
            etiket="Süre (dakika)"
            type="number"
            min={5}
            max={480}
            value={sure}
            onChange={(e) => setSure(Number(e.target.value))}
          />
        </div>

        <div>
          <Buton type="submit" disabled={katilimcilar.length === 0}>
            Müsait saat bul
          </Buton>
        </div>
      </form>

      {arandi && isPending && <p className="text-notr-600 text-sm">Aranıyor…</p>}

      {isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {error instanceof ApiHatasi ? error.message : "Müsaitlik hesaplanamadı."}
        </p>
      )}

      {data && (
        <Kart baslik="Önerilen saatler">
          {data.oneriler.length === 0 ? (
            <p className="text-notr-600 text-sm">
              Bu aralıkta {arandi?.sure} dakikalık ortak boşluk yok.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {data.oneriler.map((o) => (
                <li
                  key={o.baslangic}
                  className="rounded-alan bg-notr-100 text-notr-800 px-3 py-2 text-sm tabular-nums"
                >
                  {gun(o.baslangic)} · {saat(o.baslangic)}–{saat(o.bitis)}
                </li>
              ))}
            </ul>
          )}
        </Kart>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// KAPASITE
// ---------------------------------------------------------------

function KapasiteGorunumu() {
  const aralik = gunAraligi(new Date());

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["kapasite", aralik.baslangic],
    queryFn: () => kapasiteTablosu(aralik),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Kapasite alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.uzmanlar.length === 0) {
    return <BosDurum baslik="Kapsamınızda uzman yok" />;
  }

  return (
    <Kart baslik="Uzman kapasitesi — önümüzdeki hafta">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Uzman başına aktif öğrenci ve doluluk</caption>
          <thead>
            <tr className="border-notr-200 text-notr-600 border-b text-left text-xs">
              <th scope="col" className="py-2 pr-3 font-medium">
                Uzman
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                Aktif öğrenci
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                Dolu
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                Doluluk
              </th>
            </tr>
          </thead>
          <tbody>
            {data.uzmanlar.map((u) => (
              <tr key={u.coachId} className="border-notr-100 border-b last:border-0">
                <td className="text-notr-700 py-2 pr-3 font-mono text-xs">{u.userId}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{u.aktifOgrenci}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {Math.round(u.doluDk / 60)} sa
                </td>
                <td className="py-2 text-right tabular-nums">
                  {/*
                    Pencere sifir uzunluktaysa sunucu `null` donuyor:
                    bolen yok, OLCULEMEZ. "%0" yazmak "hic dolu degil"
                    demek olurdu ve ikisi ayri sey.
                  */}
                  {u.dolulukOrani === null ? (
                    <span className="text-notr-600">—</span>
                  ) : (
                    `%${Math.round(u.dolulukOrani * 100)}`
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Kart>
  );
}

/**
 * KURUM ETKİNLİĞİ EKLEME — F-CAL, matris §5.12
 *
 * `POST /takvim/kurum` yazılıydı, hiçbir ekran çağırmıyordu: kurum
 * takvimi GÖRÜNÜYOR ama içine kayıt KONULAMIYORDU. Tatil, sınav günü,
 * veli toplantısı gibi herkesi ilgilendiren kayıtların girileceği yer
 * yoktu.
 *
 * İzni olmayana form hiç çizilmiyor — sunucu 403 döner ve o hatayı
 * yakalayıp gizlemek yerine hiç göstermemek doğru. Çağıran ekran
 * izne bakmıyor; form kendi 403'ünü yakalayıp bir kez saklanıyor,
 * çünkü izin listesi bu bileşene taşınmıyor.
 */
function KurumEtkinligi() {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [baslik, setBaslik] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [tumGun, setTumGun] = useState(true);
  const bugun = yerelGunBasi().toISOString().slice(0, 10);
  const [gunBas, setGunBas] = useState(bugun);
  const [gunBit, setGunBit] = useState(bugun);
  const [saatBas, setSaatBas] = useState("09:00");
  const [saatBit, setSaatBit] = useState("10:00");

  const mut = useMutation({
    mutationFn: () =>
      kurumEtkinligiEkle({
        baslik: baslik.trim(),
        aciklama: aciklama.trim() || null,
        // Tüm gün kaydında saat GÖNDERİLMİYOR, günün başı ve sonu
        // gönderiliyor: "tüm gün" bir saat aralığı değil, günün
        // kendisidir ve saat yazarsak zaman diliminde kayar.
        baslangic: tumGun ? `${gunBas}T00:00:00` : `${gunBas}T${saatBas}:00`,
        bitis: tumGun ? `${gunBit}T23:59:59` : `${gunBit}T${saatBit}:00`,
        tumGun,
      }),
    onSuccess: async () => {
      setBaslik("");
      setAciklama("");
      setAcik(false);
      await qc.invalidateQueries({ queryKey: ["takvim", "kurum"] });
    },
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          Kurum etkinliği ekle
        </Buton>
      </div>
    );
  }

  const sirali = gunBit >= gunBas;

  return (
    <Kart
      baslik="Kurum etkinliği"
      aciklama="Tatil, sınav günü, veli toplantısı — herkesin takviminde görünür."
    >
      <div className="flex flex-col gap-4">
        <Alan etiket="Başlık" value={baslik} onChange={(e) => setBaslik(e.target.value)} />

        <MetinAlani
          etiket="Açıklama (isteğe bağlı)"
          rows={2}
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
        />

        <label className="text-notr-700 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tumGun} onChange={(e) => setTumGun(e.target.checked)} />
          Tüm gün
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Başlangıç günü"
            type="date"
            value={gunBas}
            onChange={(e) => setGunBas(e.target.value)}
          />
          <Alan
            etiket="Bitiş günü"
            type="date"
            value={gunBit}
            onChange={(e) => setGunBit(e.target.value)}
            hata={sirali ? undefined : "Bitiş, başlangıçtan önce olamaz."}
          />
          {!tumGun && (
            <>
              <Alan
                etiket="Başlangıç saati"
                type="time"
                value={saatBas}
                onChange={(e) => setSaatBas(e.target.value)}
              />
              <Alan
                etiket="Bitiş saati"
                type="time"
                value={saatBit}
                onChange={(e) => setSaatBit(e.target.value)}
              />
            </>
          )}
        </div>

        {mut.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Etkinlik eklenemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={mut.isPending}
            disabled={baslik.trim().length < 2 || !sirali}
            onClick={() => mut.mutate()}
          >
            Etkinliği ekle
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setAcik(false)}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * BİR KİŞİNİN TAKVİMİ — matris §5.12
 *
 * `GET /takvim/:userId` yazılıydı, hiçbir ekran çağırmıyordu.
 * Personel kartından çağrılıyor: bir kişiye bakarken "bu hafta müsait
 * mi" sorusunun cevabı orada olmalı.
 *
 * AYRINTIYI SUNUCU KARARLAŞTIRIYOR: ayrıntı izni olmayan `mod:
 * "MESGULIYET"` alıyor ve başlık alanı cevapta HİÇ bulunmuyor.
 * Ekran bunu kendisi gizlemiyor — gizleseydi alan bir gün cevaba
 * eklendiğinde sessizce sızardı.
 */
export function KisiTakvimi({ userId }: { userId: string }) {
  return <HaftalikGorunum kaynak="kisi" kisiId={userId} />;
}
